import { Spin } from 'antd';
import { useEffect, useState } from 'react';

import type { Database_Procs_GetAlgebraGoalsMetric_Item } from '@suntex.fim/api-client';
import { useFramework } from '@suntex.fim/framework';

import { AlgebraGoalsIndexChartView } from './AlgebraGoalsIndexChartView';
import type { AlgebraGoalsIndexChartProps } from './types';

/**
 * Algebra Goals Index chart component that fetches its own data.
 * Renders a loading spinner while data is being fetched.
 */
export function AlgebraGoalsIndexChart({
  id,
  level,
  year,
  grade,
  showProgressBars = false,
  ...chartProps
}: AlgebraGoalsIndexChartProps): JSX.Element {
  const { api } = useFramework();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] =
    useState<Database_Procs_GetAlgebraGoalsMetric_Item | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get_Assessment_AlgebraGoalsMetric({
          id,
          level,
          year,
          grade,
        });
        if (!cancelled) {
          setData(response.data);
        }
      } catch {
        if (!cancelled) {
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [api, id, level, year, grade]);

  if (isLoading) {
    return <Spin />;
  }

  if (!data) {
    return <div>No data available</div>;
  }

  return (
    <AlgebraGoalsIndexChartView
      data={data}
      showProgressBars={showProgressBars}
      {...chartProps}
    />
  );
}
